/* eslint-disable indent */
import React, { CSSProperties, ReactChild, useCallback, useMemo } from 'react'
import noopWrapper, { Wrapper } from 'lib/noop-wrapper'
import classNames from 'classnames'

export const calcMaxPage = (total: number, pageSize: number) => {
  return Math.floor((total - 1) / pageSize) + 1
}

interface IProps {
  currentPage?: number // 当前页
  pageSize?: number // 每页数量
  total?: number // 数据总数
  pagerCount?: number // 显示的页码按钮的数量
  showJump?: boolean
  renderLink?(curPage: number): Wrapper
  onJump?(curPage: number): void
  onClick?(curPage: number): void
  onNextPrevClick?(curPage: number, key?: string): void
  className?: string
  theme?: 'gray' | 'white'
  style?: CSSProperties
}

const Pagination: React.FC<IProps> = ({
  currentPage = 0,
  pageSize = 0,
  total = 0,
  pagerCount = 3,
  showJump = true,
  renderLink = () => noopWrapper,
  onJump,
  onClick,
  onNextPrevClick,
  className,
  theme,
  style,
}) => {
  const realCurPage = useMemo(() => currentPage - 1, [currentPage])

  const maxPage = useMemo(
    () => calcMaxPage(total, pageSize) - 1,
    [total, pageSize]
  )

  const curPagerCount = useMemo(
    () => Math.min(maxPage, pagerCount),
    [maxPage, pagerCount]
  )

  const startPage = useMemo(() => {
    const half = Math.floor(pagerCount / 2)

    let result = realCurPage - half

    if (maxPage - realCurPage <= half) {
      result = maxPage - curPagerCount
    }

    return Math.max(1, result)
  }, [realCurPage, pagerCount, maxPage])

  const renderItem = useCallback(
    ({ page, key }: { page: number; key?: string }) => {
      const enabled = page >= 0 && page <= maxPage

      return (content?: ReactChild) => (
        <React.Fragment key={key || page}>
          <li
            key={key || page}
            className={classNames(
              'pagination-item',
              'tw-relative tw-mx-3 tw-rounded-2',
              'tw-inline-flex tw-flex-nowrap',
              { 'is-active': realCurPage === page }
            )}
          >
            {(enabled ? renderLink(page + 1) : noopWrapper)(
              <span
                className={classNames(
                  'tw-inline-block tw-px-6 tw-h-28',
                  'tw-text-14 tw-leading-28 tw-text-center tw-text-black',
                  { 'tw-cursor-pointer': enabled }
                )}
                style={{ minWidth: !key ? 24 : 28 }}
                onClick={() => {
                  if (!enabled) return
                  if (content && onNextPrevClick) onNextPrevClick(page + 1, key)
                  else if (onClick) onClick(page + 1)
                }}
              >
                {content || page + 1}
              </span>
            )}
          </li>
          <style jsx>
            {`
              .pagination-item:hover {
                background: rgba(255, 204, 50, 0.12);
              }
              .pagination-item.is-active {
                background: #ffcc32;
              }
              .pagination-item:after {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                width: 200%;
                height: 200%;
                border: 1px solid rgb(201, 203, 214);
                border-radius: 2px;
                pointer-events: none;
                transform-origin: left top;
                transform: scale(0.5);
              }
              .pagination-item:hover:after {
                border-color: #f4b700;
              }
              .pagination-item.is-active:after {
                border-color: transparent;
              }
              li:first-child {
                margin-left: 0;
              }

              li:last-child {
                margin-right: 0;
              }

              li:hover :global(.DCD_Icon.hover-hidden) {
                display: none;
              }

              li:hover :global(.DCD_Icon.hover-show) {
                display: inline;
              }
            `}
          </style>
        </React.Fragment>
      )
    },
    [renderLink, realCurPage, maxPage, onClick]
  )

  return total ? (
    <React.Fragment>
      <div
        className={classNames(
          'tw-flex tw-text-12 tw-text-common-black',
          className
        )}
        style={style}
      >
        <ul className="tw-flex">
          {renderItem({ page: realCurPage - 1, key: 'prev' })(
            <i className="DCD_Icon icon_arrow_left_1 tw-text-14" />
          )}
          {renderItem({ page: 0 })()}
          {startPage > 1
            ? renderItem({
                page: Math.max(realCurPage - curPagerCount, 0),
                key: 'jump-prev',
              })(
                <React.Fragment>
                  <i className="DCD_Icon hover-hidden icon_more_24 tw-text-12" />
                  <i className="DCD_Icon hover-show tw-hidden icon_change_year_left tw-text-12" />
                </React.Fragment>
              )
            : null}
          {Array.from({ length: curPagerCount }).map((item, index) => {
            return renderItem({ page: startPage + index })()
          })}
          {startPage + curPagerCount < maxPage
            ? renderItem({
                page: Math.min(realCurPage + curPagerCount, maxPage),
                key: 'jump-next',
              })(
                <React.Fragment>
                  <i className="DCD_Icon hover-hidden icon_more_24 tw-text-12" />
                  <i className="DCD_Icon hover-show tw-hidden icon_change_year_right tw-text-12" />
                </React.Fragment>
              )
            : null}
          {maxPage >= startPage + curPagerCount
            ? renderItem({ page: maxPage })()
            : null}
          {renderItem({ page: realCurPage + 1, key: 'next' })(
            <i className="DCD_Icon icon_into_12 tw-text-14" />
          )}
        </ul>
        {showJump && (
          <div className="tw-ml-60 tw-flex tw-items-center tw-text-black">
            前往
            <input
              type="text"
              className="pagination-input tw-mx-6 tw-w-60 tw-h-28 tw-bg-transparent tw-outline-none tw-text-center tw-px-4 tw-border-1 tw-border-solid tw-border-color-gray-600 tw-rounded-2"
              onKeyDown={(e) =>
                e.keyCode === 13 &&
                onJump?.(
                  Math.min(
                    maxPage + 1,
                    Number((e.target as HTMLInputElement).value) || 1
                  )
                )
              }
              onInput={(e) => {
                e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '')
              }}
            />
            页
          </div>
        )}
      </div>
      <style jsx>
        {`
          div :global(a) {
            color: inherit;
          }
          .pagination-input:focus {
            border-color: #f4b700;
          }
        `}
      </style>
    </React.Fragment>
  ) : null
}

export default Pagination
